import type { CSSProperties, Ref, VNode, VNodeChild } from 'vue';
import { computed, h, reactive, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type {
  SelectOption,
  TransferProps,
  TreeOption,
  TreeOverrideNodeClickBehavior,
  TreeProps,
  TreeSelectProps
} from 'naive-ui';
import { NButton, NTooltip } from 'naive-ui';
import { cloneDeep } from 'lodash-es';
import _ from 'lodash';
import type { SelectProps } from 'naive-ui/es/select/src/Select';
import { addFilter, removeFilter } from '@/utils/common';
import type { Api } from '@/typings/api';
import type { FlatResponseData } from '~/packages/axios';

/** 远程下拉或穿梭框选择组件 */
type ApiFn = (args: any) => Promise<FlatResponseData>;
export interface RemoteOptionsConfig {
  apiFn: ApiFn;
  apiParams: Api.Common.CommonSearchParams;
  /** 数据转换 */
  transformer?: (data: any[]) => any[];
  labelField: string;
  /** 父级字段 */
  parentField?: string;
  /** 是否显示tooltip */
  tooltip?: boolean;
  /** 组件属性 */
  props?: Partial<SelectProps | TreeSelectProps | TransferProps>;
  /** 搜索参数处理，返回 filter数组 */
  handleSearchFilter?: (searchText: string) => Api.Common.Filter[];
}
/** hooks参数 */
export interface RemoteSelectParam {
  /** 组件 */
  component?: 'NSelect' | 'NTreeSelect' | 'NTree' | 'NTransfer' | 'NCascader';
  /** 组件model */
  model: Ref<string | string[]>;
  /** 返回值类型，为object时将返回选择的对象或数组 */
  valueType?: 'string' | 'object';
  /** 组件远程相关参数,在 constants/remote-option-config.ts中定义 */
  config: RemoteOptionsConfig;
  /** 下拉组件属性，直接传入NSelect组件 */
  props?: Partial<SelectProps | TreeSelectProps | TreeProps | TransferProps> & {
    maxlength?: number;
    style?: CSSProperties;
    /** 值变化事件 */
    onSelectChange?: (value: string | string[], valueRecord: any | any[]) => void;
  };
  /** 是否禁用 */
  disabled?: boolean;
}
export function useRemoteSelect({ component, model, config, props = {}, disabled = false }: RemoteSelectParam) {
  /** 选项数据 */
  const remoteOptions = ref<any[]>([]);

  const loading = ref<boolean>(false);

  const resultProps = getResultProps();

  // Options中值字段别名
  const valueField = getValueFieldName();

  // NTreeSelect NTree子节点字段名
  const childrenField = getChildrenFieldName();

  // 是否多选
  const multiple = 'multiple' in props ? props.multiple : component === 'NTransfer';

  // if (!disabled) {
  //   console.log('🚀 ~ useRemoteSelect ~ setOptions1');
  //   setOptions(config.apiParams);
  // }
  // watch(resultProps, val => {
  //   console.log('🚀 ~ watch ~ remote resultProps:', val);
  // });
  watch(
    model,
    async (newVal, oldVal) => {
      if (newVal) {
        switch (component) {
          case 'NTree':
            (resultProps as TreeProps).checkedKeys = newVal as string[];
            break;
          default:
            break;
        }
        if (newVal.length > 0 && !oldVal) {
          const apiParams = cloneDeep(config.apiParams);
          if (apiParams.size && apiParams.size > 0) {
            let orFilter: Api.Common.Filter;
            if (apiParams.filter.length > 0) {
              orFilter = {
                type: 'andOr',
                column: null,
                value: [
                  {
                    type: 'and',
                    column: null,
                    value: [...apiParams.filter]
                  },
                  {
                    column: valueField,
                    type: multiple ? 'andIn' : 'andEq',
                    value: newVal
                  }
                ]
              };
            } else {
              orFilter = {
                type: 'andOr',
                column: null,
                value: [
                  {
                    column: valueField,
                    type: multiple ? 'andNotIn' : 'andNeq',
                    value: newVal
                  },
                  {
                    column: valueField,
                    type: multiple ? 'andIn' : 'andEq',
                    value: newVal
                  }
                ]
              };
            }
            if (component !== 'NTransfer') {
              const order = {
                column: `${valueField} ${multiple ? 'in' : '='} ${newVal}`,
                sort: false
              };
              // eslint-disable-next-line max-depth
              if (apiParams.order) {
                apiParams.order.splice(0, 0, order);
              } else {
                apiParams.order = [order];
              }
            }
            apiParams.filter = [orFilter];
          }
          // apiParams.filter.push({
          //   column: valueField,
          //   type: multiple ? 'andIn' : 'andEq',
          //   value: newVal
          // });
          setOptions(apiParams);
          // if (unwatchModel) {
          //   unwatchModel();
          // }
        } else {
          setOptions(config.apiParams);
        }
        // } else if (oldVal && oldVal.length > 0) {
      } else {
        setOptions(config.apiParams);
      }
    },
    {
      immediate: true,
      once: true
    }
  );

  if (multiple && props.maxlength !== undefined) {
    const maxlength = props.maxlength;
    watch(
      () => (model.value ? model.value.length : 0),
      (newLength, oldLength = 0) => {
        if (newLength >= maxlength && oldLength < maxlength) {
          toggleDisabled(remoteOptions.value, true);
        } else if (newLength < maxlength && oldLength >= maxlength) {
          toggleDisabled(remoteOptions.value, false);
        }
      },
      {
        immediate: true
      }
    );
    watch(remoteOptions, () => {
      if (
        remoteOptions.value.length > 0 &&
        multiple &&
        props.maxlength !== undefined &&
        model.value &&
        model.value.length >= props.maxlength
      ) {
        toggleDisabled(remoteOptions.value, true);
      }
    });
  }

  // 选择数据改变事件，同时派发数据key及数据对象
  if ('onSelectChange' in resultProps && typeof resultProps.onSelectChange === 'function') {
    const onSelectChange = resultProps.onSelectChange;
    watch(model, (newVal, oldVal) => {
      const keyRecords = getOptionsRecord(newVal, remoteOptions.value);
      onSelectChange(
        [newVal, oldVal],
        'multiple' in resultProps && resultProps.multiple ? keyRecords : keyRecords?.[0]
      );
    });
    watch(remoteOptions, () => {
      const keyRecords = getOptionsRecord(model.value, remoteOptions.value);
      onSelectChange(
        [model.value, undefined],
        'multiple' in resultProps && resultProps.multiple ? keyRecords : keyRecords?.[0]
      );
    });
  }

  /**
   * 获取选中的数据对象
   *
   * @param keys 选中的key
   * @param restKeys 已查询到的key，用于树形选择获取子节点数据对象
   * @returns
   */
  function getOptionsRecord(keys: string | string[], options: any[] = []) {
    const selectKeys = Array.isArray(keys) ? keys : [keys];
    switch (component) {
      case 'NSelect':
        return selectKeys
          .map(key => {
            const option = options.find(item => item[valueField] === key);
            return option;
          })
          .filter(item => item !== undefined);
      default:
        return selectKeys
          .map(key => {
            let option = options.find(item => item[valueField] === key);
            if (!option) {
              options.forEach(item => {
                if (item[childrenField]) {
                  const childOption = getOptionsRecord([key], item[childrenField])?.[0];
                  if (childOption) {
                    option = childOption;
                  }
                }
              });
            }
            return option;
          })
          .filter(item => item !== undefined);
    }
  }

  // 如果树形选择，且非级联选择，则自动设置半选状态keys
  if (component === 'NTree' && 'cascade' in resultProps && !resultProps.cascade) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resultProps.indeterminateKeys = computed(() => {
      const keys: string[] = [];
      if (model.value && remoteOptions.value) {
        initIndeterminateKeys(model.value as string[], remoteOptions.value, keys);
      }
      return keys;
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resultProps['on-update:checked-keys'] = (
      keys: string[],
      _option: Array<TreeOption | null>,
      meta: { node: TreeOption | null; action: 'check' | 'uncheck' }
    ) => {
      if (meta.action === 'uncheck' && meta.node?.[childrenField]) {
        removeChildrenKeys(meta.node[valueField] as string, keys, remoteOptions.value);
      } else if (meta.action === 'check' && config.parentField && meta.node && _.get(meta.node, config.parentField)) {
        const [, parentKeys] = getParentKeys(meta.node[valueField] as string, keys, remoteOptions.value);
        keys.push(...parentKeys);
      }
      model.value = keys;
    };
  }

  /**
   * 根据最大选项数量，设置禁用状态
   *
   * @param options
   * @param disabledItem
   */
  function toggleDisabled(options: any[], disabledItem: boolean) {
    for (let i = 0; i < options.length; i += 1) {
      const item = options[i];
      if (disabledItem && !model.value!.includes(item[valueField] as string)) {
        item.disabled = true;
      } else {
        item.disabled = false;
      }
      if (childrenField !== null && item[childrenField] && item[childrenField].length > 0) {
        toggleDisabled(item[childrenField], disabledItem);
      }
    }
  }

  const debounceSetOptions = useDebounceFn(setOptions, 300);
  /** 获取远程数据 */
  // eslint-disable-next-line complexity
  async function setOptions(apiParams: Api.Common.CommonSearchParams) {
    loading.value = true;
    try {
      const {
        data: { content }
      } = await config.apiFn(apiParams);
      if (!content) {
        return;
      }
      if (config.labelField.includes('.')) {
        content.forEach((item: any) => {
          item[config.labelField] = _.get(item, config.labelField);
        });
      }
      if (config.transformer !== undefined) {
        const treeData = config.transformer(content);
        // 如果树形选择，且多选，且非级联选择，则父级添加全选按钮
        if (
          !disabled &&
          component === 'NTree' &&
          'cascade' in resultProps &&
          !resultProps.cascade &&
          resultProps.multiple
        ) {
          setChildCheckAllButton(treeData);
        }
        remoteOptions.value = treeData;
      } else {
        remoteOptions.value = content;
      }
      // 判断是否需要重置已选择数据
      if (component === 'NSelect') {
        if (remoteOptions.value.length === 0) {
          if (multiple && model.value && model.value.length > 0) {
            (model.value as []).length = 0;
          } else if (model.value) {
            model.value = '';
          }
        } else if (!multiple && model.value) {
          if (remoteOptions.value.findIndex(item => item[valueField] === model.value) === -1) {
            model.value = '';
            await setOptions(apiParams);
          }
        } else if (multiple && model.value && model.value.length > 0) {
          const values = (model.value as []).filter(
            item => remoteOptions.value.findIndex(option => option[valueField] === item) > -1
          );
          if (values && values.length === 0) {
            (model.value as []).length = 0;
            await setOptions(apiParams);
          } else if (values && values.length !== model.value.length) {
            model.value = values;
            await setOptions(apiParams);
          }
        }
      }
    } finally {
      loading.value = false;
    }
  }

  /** 获取配置 */
  function getResultProps() {
    const override: TreeOverrideNodeClickBehavior = ({ option }) => {
      if (option[childrenField]) {
        return 'toggleExpand';
      }
      return 'default';
    };
    switch (component) {
      case 'NTreeSelect':
        return reactive<Partial<TreeSelectProps>>({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          options: remoteOptions,
          labelField: config.labelField,
          clearable: true,
          filterable: true,
          maxTagCount: 'responsive',
          size: 'medium',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          loading,
          disabled,
          renderLabel: config.tooltip
            ? ({ option }) =>
                h(NTooltip, null, {
                  trigger: () => _.get(option, config.labelField),
                  default: () => _.get(option, config.labelField)
                }) as VNodeChild
            : undefined,
          ...(config.props === undefined ? {} : (config.props as Partial<TreeSelectProps>)),
          ...(props as Partial<TreeSelectProps>)
        });
      case 'NTree':
        return reactive<TreeProps>({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          data: remoteOptions,
          labelField: config.labelField,
          checkable: true,
          cascade: true,
          blockLine: true,
          multiple: true,
          showLine: true,
          virtualScroll: true,
          overrideDefaultNodeClickBehavior: override,
          // defaultCheckedKeys: model.value as string[],
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          checkedKeys: model as Ref<string[]>,
          'on-update:checked-keys': (keys: string[]) => {
            model.value = keys;
          },
          ...(config.props === undefined ? {} : (config.props as Partial<TreeProps>)),
          ...(props as Partial<TreeProps>)
        });
      case 'NTransfer':
        return reactive<TransferProps>({
          virtualScroll: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          options: remoteOptions,
          disabled,
          sourceFilterable: true,
          ...(config.props === undefined ? {} : (config.props as Partial<TransferProps>)),
          ...(props as Partial<TransferProps>)
        });
      case 'NSelect':
      default:
        return reactive<Partial<SelectProps>>({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          options: remoteOptions,
          remote: true,
          labelField: config.labelField,
          clearable: true,
          filterable: true,
          maxTagCount: 'responsive',
          size: 'medium',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          loading,
          disabled,
          renderOption: config.tooltip
            ? ({ node, option }: { node: VNode; option: SelectOption }) =>
                h(NTooltip, null, {
                  trigger: () => node,
                  default: () => _.get(option, config.labelField)
                })
            : undefined,
          onSearch: (query: string) => {
            if (query && query.trim().length > 0) {
              if (config.handleSearchFilter) {
                const searchFilters = config.handleSearchFilter(query);
                if (searchFilters.length > 0) {
                  searchFilters.forEach(filter => addFilter(config.apiParams.filter, filter));
                }
              } else {
                addFilter(config.apiParams.filter, {
                  column: config.labelField,
                  type: 'andLike',
                  value: `%${query}%`
                });
              }
            } else {
              removeFilter(config.apiParams.filter, config.labelField);
            }
            let orFilter: Api.Common.Filter | null = null;
            if (multiple && model.value?.length > 0) {
              orFilter = {
                type: 'andOr',
                column: null,
                value: [
                  {
                    type: 'and',
                    column: null,
                    value: [...config.apiParams.filter]
                  },
                  {
                    column: valueField,
                    type: 'andIn',
                    value: model.value
                  }
                ]
              };
            }
            debounceSetOptions(orFilter ? { ...config.apiParams, filter: [orFilter] } : config.apiParams);
          },
          ...(config.props === undefined ? {} : (config.props as Partial<SelectProps>)),
          ...(props as Partial<SelectProps>)
        });
    }
  }

  function getValueFieldName() {
    switch (component) {
      case 'NTreeSelect':
        return 'keyField' in resultProps && resultProps.keyField !== undefined ? resultProps.keyField : 'key';
      case 'NTree':
        return 'keyField' in resultProps && resultProps.keyField !== undefined ? resultProps.keyField : 'key';
      case 'NSelect':
      default:
        return 'valueField' in resultProps && resultProps.valueField !== undefined ? resultProps.valueField : 'value';
    }
  }

  function getChildrenFieldName() {
    switch (component) {
      case 'NTreeSelect':
      case 'NTree':
        return 'childrenField' in resultProps && resultProps.childrenField !== undefined
          ? resultProps.childrenField
          : 'children';
      default:
        return 'children';
    }
  }

  /**
   * 设置子节点全选按钮
   *
   * @param treeData 树数据
   */
  function setChildCheckAllButton(treeData: any[]) {
    treeData.forEach((item: any) => {
      if (item[childrenField] && item[childrenField].length > 0) {
        const checkAllButton = h(
          NButton,
          {
            text: true,
            type: 'primary',
            size: 'tiny',
            onClick: e => {
              e.stopPropagation();
              const checkedKeys = getChildrenKeys(item);
              if (model.value.includes(item[valueField])) {
                model.value = _.difference(model.value as string[], checkedKeys);
              } else {
                model.value = _.union(model.value as string[], checkedKeys);
              }
            }
          },
          {
            default: () => (model.value.includes(item[valueField]) ? '取消' : '全选')
          }
        );
        item.suffix = () => checkAllButton;
        setChildCheckAllButton(item[childrenField]);
      }
    });
  }
  /**
   * 获取节点及子节点的key
   *
   * @param option 节点
   * @returns keys
   */
  function getChildrenKeys(option: any): string[] {
    const childrenKeys = [option[valueField]];
    if (option[childrenField] && option[childrenField].length > 0) {
      option[childrenField].forEach((child: any) => {
        if (child[childrenField] && child[childrenField].length > 0) {
          childrenKeys.push(...getChildrenKeys(child));
        } else {
          childrenKeys.push(child[valueField]);
        }
      });
    }
    return childrenKeys;
  }

  /** Tree 半选节点 */
  /** 初始化半选节点 */
  function initIndeterminateKeys(checkedKeys: string[], options: any[], indeterminateKeys: string[]): boolean {
    let allChecked = true;
    for (const option of options) {
      if (checkedKeys.includes(option[valueField])) {
        if (option[childrenField] && option[childrenField].length > 0) {
          if (!initIndeterminateKeys(checkedKeys, option[childrenField], indeterminateKeys)) {
            indeterminateKeys.push(option[valueField]);
            allChecked = false;
          }
        }
      } else {
        allChecked = false;
      }
    }
    return allChecked;
  }

  // eslint-disable-next-line max-params
  function removeChildrenKeys(checkedKey: string, checkedKeys: string[], options: any[], doRemove = false) {
    for (const option of options) {
      if (doRemove) {
        const index = checkedKeys.indexOf(option[valueField]);
        if (index > -1) {
          checkedKeys.splice(index, 1);
          if (option[childrenField] && option[childrenField].length > 0) {
            removeChildrenKeys(checkedKey, checkedKeys, option[childrenField], true);
          }
        }
      } else if (option[childrenField] && option[childrenField].length > 0) {
        if (option[valueField] === checkedKey) {
          removeChildrenKeys(checkedKey, checkedKeys, option[childrenField], true);
          break;
        }
        removeChildrenKeys(checkedKey, checkedKeys, option[childrenField], false);
      }
    }
  }

  /**
   * 获取未选中的父级节点keys
   *
   * @param checkedKey
   * @param checkedKeys
   * @param options
   */
  function getParentKeys(checkedKey: string, checkedKeys: string[], options: any[]): [boolean, string[]] {
    for (const option of options) {
      if (option[valueField] === checkedKey) {
        return [true, []];
      } else if (option[childrenField] && option[childrenField].length > 0) {
        const [isDeep, parentKeys] = getParentKeys(checkedKey, checkedKeys, option[childrenField]);
        if (isDeep) {
          if (!checkedKeys.includes(option[valueField])) {
            parentKeys.push(option[valueField]);
          }
          return [true, parentKeys];
        }
      }
    }
    return [false, []];
  }

  return resultProps;
}
